// src/components/ClientPortalDocuments.js

import React, { useState, useCallback } from 'react';
import 'animate.css';

function ClientPortalDocuments({ currentUser, clients, documents, cases, authAxios, showFormMessage }) {
  // Find the specific client profile linked to the current user
  const clientProfile = clients.find(client => client.user === currentUser.id);

  // Filter documents relevant to this specific client profile or their cases
  const clientDocuments = documents.filter(d =>
    d.client === (clientProfile ? clientProfile.id : null) ||
    (d.case && cases.some(c => c.id === d.case && c.client === (clientProfile ? clientProfile.id : null)))
  );

  const [showDocumentVersionsModal, setShowDocumentVersionsModal] = useState(false);
  const [selectedDocumentForVersions, setSelectedDocumentForVersions] = useState(null);
  const [documentVersions, setDocumentVersions] = useState([]); // State for versions of the selected document

  // Function to fetch versions for a specific document
  const fetchDocumentVersions = useCallback(async (documentId) => {
    try {
      const response = await authAxios.get(`documents/${documentId}/versions/`);
      setDocumentVersions(response.data);
    } catch (err) {
      showFormMessage('danger', 'Failed to fetch document versions.');
      console.error("Error fetching document versions:", err);
    }
  }, [authAxios, showFormMessage]);

  // --- Document Versioning Handlers ---
  const openDocumentVersionsModal = useCallback((document) => {
    setSelectedDocumentForVersions(document);
    fetchDocumentVersions(document.id); // Fetch versions when modal opens
    setShowDocumentVersionsModal(true);
  }, [fetchDocumentVersions]);

  const closeDocumentVersionsModal = useCallback(() => {
    setSelectedDocumentForVersions(null);
    setShowDocumentVersionsModal(false);
    setDocumentVersions([]); // Clear versions when modal closes
  }, []);

  return (
    <div className="client-portal-documents animate__animated animate__fadeInUp">
      <h2 className="mb-4 text-primary">Your Documents</h2>
      {clientProfile ? (
        <p className="lead text-secondary">Access documents related to your profile and cases, {clientProfile.first_name} {clientProfile.last_name}.</p>
      ) : (
        <p className="lead text-warning">Your user account is not yet linked to a client profile. Please contact the firm administrator to view your documents.</p>
      )}

      {clientDocuments.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-hover table-striped shadow-sm rounded overflow-hidden">
            <thead className="bg-primary text-white">
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Associated Case</th>
                <th>Uploaded At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(clientDocuments || []).map(doc => (
                <tr key={doc.id}>
                  <td>{doc.title}</td>
                  <td>{doc.category}</td>
                  <td>{doc.case_title || 'N/A'}</td>
                  <td>{new Date(doc.uploaded_at).toLocaleDateString()}</td>
                  <td>
                    <a href={doc.file} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-success rounded-pill me-2">View</a>
                    <button className="btn btn-sm btn-secondary rounded-pill" onClick={() => openDocumentVersionsModal(doc)}>Versions</button>
                    {doc.share_link && (
                      <a href={doc.share_link} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary rounded-pill ms-2">Share Link</a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="alert alert-info mt-4" role="alert">
          No documents found associated with your client profile or cases.
        </div>
      )}

      {/* Document Versions Modal */}
      {showDocumentVersionsModal && selectedDocumentForVersions && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">Versions for: {selectedDocumentForVersions.title}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={closeDocumentVersionsModal} aria-label="Close"></button>
              </div>
              <div className="modal-body">
                <h6 className="text-secondary mt-4">Version History</h6>
                {(documentVersions || []).length > 0 ? (
                  <ul className="list-group">
                    {(documentVersions || []).map(version => (
                      <li key={version.id} className="list-group-item d-flex justify-content-between align-items-center">
                        <div>
                          <strong>v{version.version_number}</strong> - Uploaded {new Date(version.uploaded_at).toLocaleString()} by {version.uploaded_by_username}
                          {version.notes && <small className="d-block text-muted">Notes: {version.notes}</small>}
                        </div>
                        <a href={version.file} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary rounded-pill">View</a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted">No previous versions available.</p>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary rounded-pill shadow-sm" onClick={closeDocumentVersionsModal}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientPortalDocuments;
